import { format, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sortBy } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';

// Components
import {
  CalendarEvent,
  Calendar,
  DubbleSideButton,
  Button,
  Tag,
  DialogError,
  MOBILE_MEDIA_QUERY,
  useMediaQuery,
} from '@repo/zolintra-ui';

// Hooks
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useDialog } from '../../../hooks/useDialog';
import usePost, { POST_STATUS } from '../../../hooks/domain/usePost';
import useResources from '../../../hooks/domain/useResources';
import useMonthSelector from '../../../hooks/useMonthSelector';
import useSearchParams from '../../../hooks/useSearchParams';
import { weekDays } from '../../../hooks/useWeekDays';
import { useAppContext } from '../../../store/AppContext';

// Permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';

// Services
import { usePostsListQuery } from '../../../services/post/postQueries';
import { useResourcesStaffListQuery, useResourcesListQuery } from '../../../services/resource/resourceQueries';

// Styles
import styles from './PlanningSubView.module.css';
import { PlanningAgenda } from './PlanningAgenda';

// New
import NewPostDialog from '../dialogTemplates/NewPostDialog';
import NewRemoteWorkingForm from '../formTemplates/NewRemoteWorkingForm';
import NewLeaveForm from '../formTemplates/NewLeaveForm';

// Read
import ReadRemoteWorkingForm from '../formTemplates/ReadRemoteWorkingForm';
import ReadLeaveForm from '../formTemplates/ReadLeaveForm';

// Constants
import { VIEW_DOMAIN } from '../../../constants';

const dateFnsLocales = {
  fr,
};

function PlanningSubView() {
  // Hooks
  const [{ user }] = useAppContext();
  const { getSearchParams } = useSearchParams();
  const { t } = useTranslation();
  const {
    getSlotsIdFromMonth,
    getSlotsIdFromDate,
    doPeriodsOverlap,
    timesOfDayOptions,
    getPostStatusTagStyling,
  } = usePost();
  const { setDialog } = useDialog();
  const {
    getResourceDisplayName,
    getResourcePosition,
    getStaffResourcesListWithUserInfo,
  } = useResources();
  const { handleMonthSelectorClick, setMonthFilterOnToday } = useMonthSelector();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [todayFocus, setTodayFocus] = useState(0);

  const handleToday = () => {
    setMonthFilterOnToday();
    setTodayFocus((focus) => focus + 1);
  };

  // States
  const {
    year = new Date().getFullYear().toString(),
    month = new Date().getMonth().toString(),
  } = getSearchParams();
  const viewMonthSlotsId = getSlotsIdFromMonth({ month: Number(month), year });

  // Permissions
  const hasAnyPostAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.GET_ANY_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );
  const hasAtLeastOnePostCreationPermission = (
    hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_LEAVE_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_REMOTE_WORKING_POST, user.prm)
  );
  const hasConfigurationPermission = (
    (
      hasPermission(PERMISSIONS.GET_CONFIGURATION, user.prm)
      && hasPermission(PERMISSIONS.UPDATE_CONFIGURATION, user.prm)
    ) || (
      hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
      && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
      && hasPermission(PERMISSIONS.UPDATE_ANY_RESOURCE, user.prm)
    )
  );

  // API Calls
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasAnyPostAndResourcePermission,
  });
  const resourcesStaffListQuery = useResourcesStaffListQuery({
    enabled: hasAnyPostAndResourcePermission,
  });

  const postListQuery = usePostsListQuery(
    { year },
    {
      includedStartSlotId: viewMonthSlotsId.startSlotId,
      excludedEndSlotId: viewMonthSlotsId.endSlotId + 1,
      type: null,
      status: null,
      resourceId: hasAnyPostAndResourcePermission ? null : user.id,
      businessUnit: null,
    },
    {
      select: (data) => {
        const postsData = data?.data || [];
        const newData = postsData
          .sort((a, b) => a.includedStartSlotId - b.includedStartSlotId)
          .reduce((acc, post) => {
            const { resourceId, status } = post;
            const newResourcePosts = [].concat(acc[resourceId] || [], post);
            return status !== POST_STATUS.REFUSED ? {
              ...acc,
              [resourceId]: newResourcePosts,
            } : acc;
          }, {});

        /**
         * This function processes the newData object to create a new data structure.
         * It iterates over each resource and its posts, checks for overlapping posts,
         * and assigns them to different rows.
         */
        const newDataRows = Object.keys(newData).reduce((accResource, resourceId) => {
          // Get the posts for the current resource
          const resourcePosts = newData[resourceId];

          // Process the posts for the current resource
          const rows = resourcePosts.reduce((acc, post) => {
            let firstFalse = null;

            // Determine the row position for the current post
            const rowPosition = Object.keys(acc).reduce((accRow, rowPost) => {
              // Check if the current post overlaps with any previous posts in the same row
              const isOverlappingPost = acc[rowPost].some(
                (prevPost) => doPeriodsOverlap(prevPost, post),
              );

              // If the post not overlap and we haven't found a no-overlapping use the current row
              if (!isOverlappingPost && firstFalse === null) {
                firstFalse = rowPost;
                return rowPost;
              }

              // If the post overlaps and we haven't found a non-overlapping ++ the row number
              if (isOverlappingPost && firstFalse === null) {
                return rowPost.replace(/-(\d+)/, (match, number) => `-${parseInt(number, 10) + 1}`);
              }

              return accRow;
            }, 'row-1');

            return {
              ...acc,
              [rowPosition || 'row-1']: [...(acc[rowPosition || 'row-1'] || []), post],
            };
          }, {});

          const newResourcePost = Object.entries(rows).reduce((acc, [rowKey, items]) => {
            const itemsWithGridRow = items.map((item) => ({ ...item, row: +rowKey.replace('row-', '') }));
            return [...acc, ...itemsWithGridRow];
          }, []);

          return {
            ...accResource,
            [resourceId]: newResourcePost,
          };
        }, {});

        return newDataRows;
      },
    },
  );

  // Handlers
  const handleDayClick = (datesSelected, resource) => {
    if (!datesSelected?.length) return;
    const selectionStartDate = datesSelected[0] || null;
    const selectionEndDate = datesSelected[1] || datesSelected[0] || null;

    setDialog({
      type: 'SHOW_DIALOG',
      payload: {
        title: t('remoteWorkingAndLeave.newPostDialog.title'),
        children: (
          <ErrorBoundary FallbackComponent={DialogError}>
            <NewPostDialog
              forms={{
                [VIEW_DOMAIN.REMOTE_WORKING]: (
                  <NewRemoteWorkingForm
                    event={{
                      startDate: format(new Date(selectionStartDate), 'yyyy-MM-dd'),
                      endDate: format(new Date(selectionEndDate), 'yyyy-MM-dd'),
                      resourceId: { label: getResourceDisplayName(resource), value: resource.id },
                    }}
                    isAdminView
                  />
                ),
                [VIEW_DOMAIN.LEAVE]: (
                  <NewLeaveForm
                    event={{
                      startDate: format(new Date(selectionStartDate), 'yyyy-MM-dd'),
                      endDate: format(new Date(selectionEndDate), 'yyyy-MM-dd'),
                      startDateSlot: timesOfDayOptions[0].value,
                      endDateSlot: timesOfDayOptions[1].value,
                      resourceId: { label: getResourceDisplayName(resource), value: resource.id },
                    }}
                    isAdminView
                  />
                ),
              }}
            />
          </ErrorBoundary>
        ),
        onClose: () => {
          setDialog({ type: 'HIDE_DIALOG' });
        },
      },
    });
  };

  const handleEventClick = (event) => {
    setDialog({
      type: 'SHOW_DIALOG',
      payload: {
        title: hasAnyPostAndResourcePermission ? `${t(
          `remoteWorkingAndLeave.read${event.type}Form.title`,
        )} (${getResourceDisplayName(
          resourcesListQuery.data?.find((res) => res.id === event.resourceId),
        )})` : t(`remoteWorkingAndLeave.read${event.type}Form.title`),
        topRightContent: (
          <Tag
            color={getPostStatusTagStyling(event.status).color}
            size="medium"
            className={styles['ws-nowrap']}
          >
            {t(getPostStatusTagStyling(event.status).key)}
          </Tag>),
        children: (
          <>
            {event.type === VIEW_DOMAIN.REMOTE_WORKING ? (
              <ReadRemoteWorkingForm postData={event} />
            ) : null}
            {event.type === VIEW_DOMAIN.LEAVE ? (
              <ReadLeaveForm postData={event} />
            ) : null}
          </>
        ),
        onClose: () => {
          setDialog({ type: 'HIDE_DIALOG' });
        },
      },
    });
  };

  const handleNewPostBtnClick = () => {
    setDialog({
      type: 'SHOW_DIALOG',
      payload: {
        title: t('remoteWorkingAndLeave.newPostDialog.title'),
        children: (
          <ErrorBoundary FallbackComponent={DialogError}>
            <NewPostDialog
              forms={{
                [VIEW_DOMAIN.REMOTE_WORKING]: (
                  <NewRemoteWorkingForm isAdminView />
                ),
                [VIEW_DOMAIN.LEAVE]: (
                  <NewLeaveForm isAdminView />
                ),
              }}
            />
          </ErrorBoundary>
        ),
        onClose: () => {
          setDialog({ type: 'HIDE_DIALOG' });
        },
      },
    });
  };

  const resourcesListForCalendar = useMemo(() => {
    const list = hasAnyPostAndResourcePermission ? sortBy(
      getStaffResourcesListWithUserInfo(
        resourcesStaffListQuery.data,
        resourcesListQuery.data,
      ),
      [
        (res) => (res.id === user.id ? -1 : 1),
        'firstName',
      ],
    ) : [user];

    return list.map(
      (resource) => ({
        ...resource,
        name: getResourceDisplayName(resource),
        position: getResourcePosition(resource),
      }),
    );
  }, [resourcesStaffListQuery?.data, resourcesListQuery?.data]);

  const canViewEventDetails = ({ type, resourceId }) => {
    // ANY
    const anyPermissions = {
      [VIEW_DOMAIN.REMOTE_WORKING]: PERMISSIONS.GET_ANY_REMOTE_WORKING_POST,
      [VIEW_DOMAIN.LEAVE]: PERMISSIONS.GET_ANY_LEAVE_POST,
    };
    if (hasPermission(anyPermissions[type], user.prm)) return true;

    // OWN
    const ownPermissions = {
      [VIEW_DOMAIN.REMOTE_WORKING]: PERMISSIONS.GET_OWN_REMOTE_WORKING_POST,
      [VIEW_DOMAIN.LEAVE]: PERMISSIONS.GET_OWN_LEAVE_POST,
    };
    const isPostForConnectedUser = resourceId === user.id;
    if (hasPermission(ownPermissions[type], user.prm) && isPostForConnectedUser) return true;

    return false;
  };

  const isDayClickDisabled = ({ day, resource }) => {
    // part time (only keep the whole days, not the half days)
    const partTimeDaysIndexes = resource.partTimeSchedule?.filter((time) => time.duration === 'WholeDay')?.map((time) => weekDays.indexOf(time.day) + 1) || [];
    if (partTimeDaysIndexes.some((index) => index === day.date.getDay())) return true;

    const isPast = isBefore(day.date, startOfDay(new Date()));
    const hasAnytimePermission = hasPermission(PERMISSIONS.CREATE_POST_ANYTIME, user.prm);

    return !hasAtLeastOnePostCreationPermission || (!hasAnytimePermission && isPast);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.caption}>
        <div className={styles['month-nav']}>
          <DubbleSideButton
            onLeftClick={() => handleMonthSelectorClick('previous')}
            onRightClick={() => handleMonthSelectorClick('next')}
            label={format(new Date(+year, Number(month)), 'MMMM yyyy', { locale: dateFnsLocales.fr })}
          />
          <Button
            onClick={handleToday}
            color="transparent"
            className="margin-left-m margin-right-m"
          >
            <span className="font-bold">
              {t('remoteWorkingAndLeave.today')}
            </span>
          </Button>
        </div>

        <div className={styles['action-nav']}>
          {hasConfigurationPermission ? (
            <a href="/settings">
              <Button
                color="primary500"
                shape="rounded"
                icon="setting"
                variant="bordered"
                fontWeight="bold"
                onClick={() => {}}
              >
                {t('remoteWorkingAndLeave.settingsBtnLabel')}
              </Button>
            </a>
          ) : null}

          {hasAtLeastOnePostCreationPermission ? (
            <Button
              onClick={handleNewPostBtnClick}
              color="primary500"
              shape="rounded"
              variant="filled"
              fontWeight="bold"
            >
              {t('remoteWorkingAndLeave.newBtnLabel')}
            </Button>
          ) : null}
        </div>
      </div>

      {isMobile ? (
        <PlanningAgenda
          year={year}
          month={month}
          resources={resourcesListForCalendar}
          postsByResource={postListQuery.data}
          getSlotsIdFromDate={getSlotsIdFromDate}
          getPostStatusTagStyling={getPostStatusTagStyling}
          isDayClickDisabled={isDayClickDisabled}
          onDayClick={handleDayClick}
          onEventClick={handleEventClick}
          canViewEventDetails={canViewEventDetails}
          todayFocus={todayFocus}
          t={t}
        />
      ) : (
        <Calendar
          month={Number(month)}
          year={year}
          resourcesList={resourcesListForCalendar}
          handleDayClick={handleDayClick}
          isDayClickDisabled={isDayClickDisabled}
          monthSelector={(
            <DubbleSideButton
              onLeftClick={() => handleMonthSelectorClick('previous')}
              onRightClick={() => handleMonthSelectorClick('next')}
              label={format(new Date(+year, Number(month)), 'MMMM yyyy', { locale: dateFnsLocales.fr })}
              size="small"
              className="fadeIn"
            />
        )}
        >
          {(resourceId, slotsIdColumns) => postListQuery.data?.[resourceId]?.map(
            (event) => {
              const {
                id: idEvent,
                type,
                includedStartSlotId,
                excludedEndSlotId,
                row,
                status,
                isExceptional,
              } = event;

              /**
             * This function finds the nearest day of slots in the calendar
             *
             * @param {Object} slotsId - The slotsId object
             * @param {Number} id - The id of the slot
             * @param {Boolean} increment - If we need to increment or decrement the id
             */
              const findNearDayOfSlots = (slotsId, id, increment = false) => {
                if (!id) return {};
                const step = increment ? 1 : -1;
                let i = id;
                while (!slotsId[i] && i >= 0) {
                  i += step;
                }
                return slotsId[i];
              };

              const calendarSlotStart = findNearDayOfSlots(
                slotsIdColumns,
                includedStartSlotId,
                true,
              );
              const calendarSlotEnd = findNearDayOfSlots(slotsIdColumns, excludedEndSlotId - 1);
              const isEventInWeekDaysColumns = calendarSlotStart?.column && calendarSlotEnd?.column;

              if (!isEventInWeekDaysColumns) return null;

              return (
                <CalendarEvent
                  key={idEvent}
                  type={type}
                  status={status}
                  column={{
                  // We add + 1 to the column because the first column is the user column
                    start: calendarSlotStart.column + 1,
                    // We add + 2 because of previous comment,
                    // and in CSS the end of the column is excluded so we need to add an
                    // extra column
                    end: calendarSlotEnd.column + 2,
                  }}
                  startTime={calendarSlotStart.time}
                  endTime={calendarSlotEnd.time}
                  row={row}
                  onClick={() => handleEventClick(event)}
                  disabled={!canViewEventDetails({ type, resourceId })}
                  highlight={isExceptional}
                />
              );
            },
          )}
        </Calendar>
      )}
    </div>
  );
}

PlanningSubView.propTypes = {};

export default PlanningSubView;
